import { FC, useEffect, useState } from 'react'

import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import { PrimaryInfoModal } from 'src/Components/PrimaryInfoModal'
import {
  OrderStatus,
  useAbandonCheckoutMutation,
  useGetCurrentOrderQuery,
} from 'src/Redux/artistOrderApi'

import { isResumableCheckoutUrl } from './isResumableCheckoutUrl'

const StyledModal = styled(PrimaryInfoModal)`
  max-width: 400px;
`

const Content = styled.div`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  padding: 24px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`

const Text = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-family: 'Segoe UI';
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
`

const ButtonsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 24px;
`

const StyledCTAButton = styled(CTAButton)`
  padding: 8px 40px;
`

const StyledPrimaryDottedButton = styled(PrimaryDottedButton)`
  width: fit-content;
`

const UNPAID_ORDER_STATUSES = [
  OrderStatus.Active,
  OrderStatus.Checkout,
] as const

const isUnpaidOrderStatus = (status?: OrderStatus): boolean =>
  UNPAID_ORDER_STATUSES?.some((unpaidStatus) => unpaidStatus === status)

export const OrderCheckoutRecoveryModal: FC = () => {
  const [searchParams] = useSearchParams()

  const [isDismissed, setIsDismissed] = useState(false)
  const [isInitialOrderCaptured, setIsInitialOrderCaptured] = useState(false)
  const [hadAbandonedCheckoutOnLoad, setHadAbandonedCheckoutOnLoad] =
    useState(false)
  const [hasHandledPaymentReturn, setHasHandledPaymentReturn] = useState(false)
  const [isReturnedFromPaymentWithOrder, setIsReturnedFromPaymentWithOrder] =
    useState(false)

  const { data: order, isSuccess, isFetching } = useGetCurrentOrderQuery(void 0)

  const [abandonCheckout, { isLoading }] = useAbandonCheckoutMutation()

  const isPaymentFlow = searchParams.has('paymentStatus')
  const isSuccessPayment = searchParams.get('paymentStatus') === 'success'
  const isCheckoutStatus = order?.status === OrderStatus.Checkout
  const isOrderUnpaid = isUnpaidOrderStatus(order?.status)

  useEffect(() => {
    if (!isSuccess || isFetching || isInitialOrderCaptured) {
      return
    }

    setIsInitialOrderCaptured(true)
    setHadAbandonedCheckoutOnLoad(isCheckoutStatus && !isPaymentFlow)
  }, [
    isCheckoutStatus,
    isFetching,
    isInitialOrderCaptured,
    isPaymentFlow,
    isSuccess,
  ])

  useEffect(() => {
    if (!isInitialOrderCaptured || !hadAbandonedCheckoutOnLoad) {
      return
    }

    if (!isCheckoutStatus) {
      setHadAbandonedCheckoutOnLoad(false)
    }
  }, [hadAbandonedCheckoutOnLoad, isCheckoutStatus, isInitialOrderCaptured])

  useEffect(() => {
    if (hasHandledPaymentReturn || !isPaymentFlow || !isSuccess) {
      return
    }

    if (!order) {
      return
    }

    setHasHandledPaymentReturn(true)
    setIsReturnedFromPaymentWithOrder(isUnpaidOrderStatus(order.status))
  }, [hasHandledPaymentReturn, isPaymentFlow, isSuccess, order])

  const shouldShowAbandonedCheckout =
    hadAbandonedCheckoutOnLoad && isCheckoutStatus

  const shouldShowPaymentReturn =
    !isSuccessPayment && isReturnedFromPaymentWithOrder && isOrderUnpaid

  const shouldShowModal = shouldShowAbandonedCheckout || shouldShowPaymentReturn

  const isOpen = !isDismissed && shouldShowModal

  const canResumePayment = isResumableCheckoutUrl(order?.checkoutSessionUrl)

  const handleResumePayment = (): void => {
    if (!canResumePayment) {
      return
    }

    window.location.href = String(order?.checkoutSessionUrl)
  }

  const handleResetOrder = async (): Promise<void> => {
    if (!order) {
      return
    }

    try {
      await abandonCheckout({ orderId: order.id }).unwrap()
    } finally {
      setIsDismissed(true)
    }
  }

  const handleClose = (): void => {
    void 0
  }

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={handleClose}
      closable={false}
      maskClosable={false}
      keyboard={false}
      title='Finish your payment'
    >
      <Content>
        <Text>
          {canResumePayment
            ? 'You have a campaign waiting for payment. Resume the payment or reset the order to start over.'
            : 'You have a campaign waiting for payment. Reset the order to start over.'}
        </Text>

        <ButtonsContainer>
          {canResumePayment && (
            <StyledCTAButton onClick={handleResumePayment}>
              Resume payment
            </StyledCTAButton>
          )}

          <StyledPrimaryDottedButton
            onClick={handleResetOrder}
            disabled={isLoading}
          >
            Reset order
          </StyledPrimaryDottedButton>
        </ButtonsContainer>
      </Content>
    </StyledModal>
  )
}
